import React from "react";
import { useTheme } from "@/contexts/ThemeContext";

// Real CORE Realty & Associates brand marks (public/brand/*.svg)
const DEFAULT_LOGO_BLACK = "/brand/core-logo-black.svg";

/**
 * Brokerage logo. If the brokerage has uploaded a custom logo via the
 * Brokerage Theme settings, render it as an <img>. Otherwise fall back
 * to the real CORE Realty & Associates SVG (public/brand/core-logo-black.svg).
 *
 * Variants: "mark" | "full" | "inline"
 */
export default function CoreLogo({
  variant = "mark",
  className = "",
  size = 56,
  title,
  ...props
}) {
  const { theme, brokerageName } = useTheme() || {};
  const customLogo = theme?.logo_data_url;
  const label = title || brokerageName || "CORE Realty & Associates";
  const src = customLogo || DEFAULT_LOGO_BLACK;
  // Real SVG has ~1.46 aspect ratio (wordmark stacked). Enforce sensible max widths per variant.
  const isDefault = !customLogo;

  if (variant === "inline") {
    return (
      <span className={`inline-flex items-center gap-2 ${className}`} {...props}>
        <img
          src={src}
          alt={label}
          style={{ height: size, width: "auto", maxWidth: size * (isDefault ? 1.6 : 1.5) }}
        />
        {customLogo && brokerageName ? (
          <span className="font-serif text-base leading-none">{brokerageName}</span>
        ) : null}
      </span>
    );
  }

  if (variant === "full") {
    return (
      <div className={`inline-flex flex-col items-center ${className}`} {...props}>
        <img
          src={src}
          alt={label}
          style={{ height: size, width: "auto", maxWidth: size * (isDefault ? 2.2 : 3) }}
        />
        {customLogo && brokerageName ? (
          <span className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] opacity-80">{brokerageName}</span>
        ) : null}
      </div>
    );
  }

  // "mark" (default)
  return (
    <img
      src={src}
      alt={label}
      className={className}
      style={{ height: size, width: "auto", maxWidth: size * (isDefault ? 1.6 : 1.6) }}
      {...props}
    />
  );
}
